import { isPwaEnabled } from './pwaConfig';

/**
 * Registers the minimal service worker (`/sw.js`) that makes the web app
 * installable. Safe no-op in unsupported / non-http contexts (e.g. the Figma
 * plugin's `data:`/`null`-origin bundle). See `public/sw.js` — it does no
 * caching, so it never interferes with Vite HMR or serves stale assets.
 *
 * Gated by `isPwaEnabled()` (VITE_PWA_ENABLED, default off). When the PWA is
 * disabled we don't just skip registration — we actively *unregister* any
 * worker a returning user already installed, so the app stops being
 * installable and no stale worker lingers. Re-enable by setting
 * VITE_PWA_ENABLED=true and redeploying.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  // Only over http(s) — never in the plugin's data:/null-origin context.
  if (!window.location.protocol.startsWith('http')) return;

  if (!isPwaEnabled()) {
    // Tear down any previously-registered worker so existing installs / install
    // prompts go away on the next visit.
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) => registrations.forEach((registration) => registration.unregister()))
      .catch(() => {
        // Best-effort cleanup — ignore.
      });
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((error) => {
      console.warn('Service worker registration failed:', error);
    });
  });
}
